export const BITBUCKET_PROJECT_SLUG_ANNOTATION = 'bitbucket.org/project-slug';
export const BITBUCKET_PIPELINES_BRANCH_ANNOTATION =
  'bitbucket.org/pipelines-branch';

export type PipelineRun = {
  uuid: string;
  build_number: number;
  state: {
    name: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | string;
    result?: { name: string };
    stage?: { name: string };
  };
  target: {
    ref_name?: string;
    commit?: { hash: string };
    pullrequest?: { id: number };
    selector?: { pattern?: string };
  };
  trigger?: { name: string };
  creator?: { display_name: string };
  created_on: string;
  duration_in_seconds?: number;
};

export type RunStatus =
  | 'ok'
  | 'error'
  | 'running'
  | 'pending'
  | 'aborted';

export type RunRow = {
  id: string;
  number: number;
  status: RunStatus;
  statusLabel: string;
  ref: string;
  commit: string;
  trigger: string;
  creator: string;
  startedAt: Date;
  duration: string;
  url: string;
};

export function runStatus(state: PipelineRun['state']): RunStatus {
  if (state.name === 'PENDING') {
    return 'pending';
  }
  if (state.name === 'IN_PROGRESS') {
    return state.stage?.name === 'PAUSED' ? 'pending' : 'running';
  }
  switch (state.result?.name) {
    case 'SUCCESSFUL':
      return 'ok';
    case 'STOPPED':
    case 'EXPIRED':
      return 'aborted';
    default:
      return 'error';
  }
}

export function formatDuration(seconds: number | undefined): string {
  if (seconds === undefined) {
    return '';
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function toRunRow(run: PipelineRun, projectSlug: string): RunRow {
  const label =
    run.state.result?.name ?? run.state.stage?.name ?? run.state.name;
  const ref = run.target.pullrequest
    ? `PR #${run.target.pullrequest.id}`
    : run.target.ref_name ?? '';
  return {
    id: run.uuid,
    number: run.build_number,
    status: runStatus(run.state),
    statusLabel: label.toLowerCase().replace(/_/g, ' '),
    ref,
    commit: run.target.commit?.hash.slice(0, 8) ?? '',
    trigger: (run.trigger?.name ?? '').toLowerCase(),
    creator: run.creator?.display_name ?? '',
    startedAt: new Date(run.created_on),
    duration: formatDuration(run.duration_in_seconds),
    url: `https://bitbucket.org/${projectSlug}/pipelines/results/${run.build_number}`,
  };
}

export function filterByBranch(
  runs: PipelineRun[],
  branch: string | undefined,
): PipelineRun[] {
  if (!branch) {
    return runs;
  }
  return runs.filter(run => run.target.ref_name === branch);
}
